/**
 * Mutation defaults for every queueable write.
 *
 * This is the piece that makes offline capture actually work. A mutation
 * restored from disk carries only its key and its variables — the function it
 * was built with cannot be serialised. Without a matching `setMutationDefaults`
 * the rehydrated mutation has no `mutationFn`, so `resumePausedMutations`
 * quietly does nothing and the user's write is gone. Registering defaults for
 * every key in OFFLINE_MUTATION_KEYS is what turns the persisted record back
 * into a runnable request.
 *
 * The kinds match mobile's one-for-one; only the endpoints behind them differ,
 * because the two clients complete a task through different routes.
 */
import type { QueryClient } from '@tanstack/react-query'
import { ApiError } from '@/lib/api/api-client'
import { tasksApi } from '@/lib/api/tasks.api'
import { threadApi } from '@/lib/api/threads.api'
import { timeEntryApi } from '@/lib/api/time-entries.api'
import { useOrganizationStore } from '@/store/organization.store'

import type {
  CreateTask,
  CreateThreadComment,
  CreateTimeEntry,
  TaskWithAssignments,
  ThreadComment,
  TimeEntry,
  UpdateTaskInput,
} from '@scrambl/shared'

import type { OfflineMutationEnvelope } from './offline-queue'
import { offlineRunnerContext } from './ledger'
import {
  classifyReplayError,
  createOfflineMutationFn,
  OFFLINE_MUTATION_KEYS,
  OFFLINE_QUEUE_MAX_ATTEMPTS,
} from './offline-queue'

export interface TaskCreatePayload {
  data: CreateTask
}

/**
 * Web completes a task by patching its status field rather than through a
 * dedicated status route, so the payload is a resolved update body. Replaying
 * it re-asserts the same value, which is what keeps the kind idempotent.
 */
export interface TaskStatusPayload {
  taskId: string
  data: UpdateTaskInput
}

export interface TimeEntryCreatePayload {
  data: CreateTimeEntry
}

export type ThreadCommentPayload = CreateThreadComment & {
  moduleType?: string
  moduleId?: string
}

const { getStatus } = offlineRunnerContext

/**
 * Refuse to replay a write into a different organisation than it was made in.
 *
 * On web the org is a request *header* applied globally by the API client, not
 * a parameter on the call — so a write queued in one org and replayed after the
 * user switches would silently land in the other one. Mobile passes the org id
 * explicitly on every request and cannot hit this, but the guard lives in both
 * clients' defaults so the rule is stated in one obvious place.
 */
function withOrgGuard<TPayload, TResult>(
  run: (envelope: OfflineMutationEnvelope<TPayload>) => Promise<TResult>,
): (envelope: OfflineMutationEnvelope<TPayload>) => Promise<TResult> {
  return envelope => {
    const currentOrgId =
      useOrganizationStore.getState().currentOrganization?.id ?? ''
    if (envelope.organizationId && envelope.organizationId !== currentOrgId) {
      throw new ApiError(
        'This change was made in a different workspace. Switch back to send it.',
        409,
      )
    }
    return run(envelope)
  }
}

/**
 * `networkMode: 'online'` matters more than it looks. It parks the mutation
 * *before* `mutationFn` touches the network, so a paused write is provably
 * un-sent and replaying it carries no duplicate risk. The alternative
 * (`offlineFirst`) would run the request anyway, fail on the dead socket, and
 * leave us unable to tell "never sent" from "sent, reply lost".
 */
const sharedDefaults = {
  networkMode: 'online' as const,
  retry: (failureCount: number, error: unknown) =>
    failureCount < OFFLINE_QUEUE_MAX_ATTEMPTS - 1 &&
    classifyReplayError(getStatus(error)) === 'retry',
  retryDelay: (attemptIndex: number) =>
    Math.min(1000 * 2 ** attemptIndex, 30000),
  // Replayed writes are silent. The pending-writes popover is where their
  // outcome is reported; a toast for something the user did in a tunnel two
  // hours ago is noise they cannot act on.
  meta: { silent: true },
}

export function registerOfflineMutationDefaults(
  queryClient: QueryClient,
): void {
  queryClient.setMutationDefaults([...OFFLINE_MUTATION_KEYS['task.create']], {
    ...sharedDefaults,
    mutationFn: createOfflineMutationFn<TaskCreatePayload, TaskWithAssignments>(
      'task.create',
      withOrgGuard(envelope => tasksApi.createTask(envelope.payload.data)),
      offlineRunnerContext,
    ) as (variables: unknown) => Promise<TaskWithAssignments>,
  })

  queryClient.setMutationDefaults([...OFFLINE_MUTATION_KEYS['task.status']], {
    ...sharedDefaults,
    mutationFn: createOfflineMutationFn<TaskStatusPayload, TaskWithAssignments>(
      'task.status',
      withOrgGuard(envelope =>
        tasksApi.updateTask(envelope.payload.taskId, envelope.payload.data),
      ),
      offlineRunnerContext,
    ) as (variables: unknown) => Promise<TaskWithAssignments>,
  })

  queryClient.setMutationDefaults(
    [...OFFLINE_MUTATION_KEYS['time-entry.create']],
    {
      ...sharedDefaults,
      mutationFn: createOfflineMutationFn<TimeEntryCreatePayload, TimeEntry>(
        'time-entry.create',
        withOrgGuard(envelope =>
          timeEntryApi.createTimeEntry(envelope.payload.data),
        ),
        offlineRunnerContext,
      ) as (variables: unknown) => Promise<TimeEntry>,
    },
  )

  queryClient.setMutationDefaults(
    [...OFFLINE_MUTATION_KEYS['thread-comment.create']],
    {
      ...sharedDefaults,
      mutationFn: createOfflineMutationFn<ThreadCommentPayload, ThreadComment>(
        'thread-comment.create',
        withOrgGuard(envelope =>
          threadApi.createThreadComment({
            ...envelope.payload,
            // The envelope's idempotency key *is* the comment's
            // clientMessageId. The server holds a UNIQUE on
            // (threadId, authorId, clientMessageId) and returns the existing
            // row on conflict, so a replay is deduped server-side rather than
            // relying on our ledger alone.
            clientMessageId: envelope.idempotencyKey,
          }),
        ),
        offlineRunnerContext,
      ) as (variables: unknown) => Promise<ThreadComment>,
    },
  )
}
